/**
 * Recognizer for the companion activation gestures. Key events arrive
 * pre-filtered to the Ctrl+Alt chord by core/activation-chord.
 *
 * Pure timing state machine — no DOM, no React — so the fiddly tap/hold/
 * double-tap logic is unit-testable. The overlay feeds it raw key events and
 * wires the callbacks:
 *
 *   - HOLD    (key held past `holdDelayMs`)              → onHoldStart / onHoldEnd
 *   - DOUBLE  (two quick taps within `doubleTapMs`)      → onDoubleTap
 *   - blur while holding                                 → onCancel
 *
 * A single tap does nothing: it switched Ask and Build until Build went away
 * (2026-10-16, one-assistant Phase 2d).
 *
 * A "bare" tap means the chord was pressed and released with no other key in
 * between. If any other key fired during the press (e.g. Ctrl+Alt+T), it was a
 * shortcut, NOT a tap, so double-tap detection is suppressed. This keeps
 * incidental chord usage from toggling the buddy.
 */

export interface CtrlGestureCallbacks {
  /** Key held long enough to start push-to-talk. */
  onHoldStart: () => void
  /** Held key released (normal end of push-to-talk). */
  onHoldEnd: () => void
  /** Two quick bare taps — toggle the buddy on/off. */
  onDoubleTap: () => void
  /** Focus lost while holding — discard the in-flight recording. */
  onCancel: () => void
}

export interface CtrlGestureConfig {
  doubleTapMs?: number
  tapMaxHoldMs?: number
  holdDelayMs?: number
}

export interface CtrlGesture {
  /** Activation key pressed (caller already filtered key + input-focus). */
  keyDown(): void
  /** Some OTHER key pressed — marks the current press as a shortcut. */
  otherKeyDown(): void
  /** Activation key released. */
  keyUp(): void
  /** Window blurred — cancel any hold/pending timers. */
  blur(): void
  /** Tear down timers without firing callbacks (effect cleanup). */
  dispose(): void
}

export function createCtrlGesture(
  cb: CtrlGestureCallbacks,
  config: CtrlGestureConfig = {},
): CtrlGesture {
  const DOUBLE_TAP_MS = config.doubleTapMs ?? 400
  const TAP_MAX_HOLD_MS = config.tapMaxHoldMs ?? 300
  const HOLD_DELAY_MS = config.holdDelayMs ?? 250

  const NO_TAP = Number.NEGATIVE_INFINITY

  let isHolding = false
  let keyIsDown = false
  let keyDownTime = 0
  let lastTapUpTime = NO_TAP // sentinel: no prior tap in the window
  let otherKeyPressed = false
  let holdTimer: ReturnType<typeof setTimeout> | null = null

  const clearHold = () => {
    if (holdTimer) {
      clearTimeout(holdTimer)
      holdTimer = null
    }
  }

  return {
    keyDown() {
      otherKeyPressed = false
      keyIsDown = true
      keyDownTime = Date.now()

      // Second press of a potential double-tap: don't arm a hold.
      if (Date.now() - lastTapUpTime < DOUBLE_TAP_MS) return

      // Arm hold-to-talk: if the key is still down after the delay, start.
      clearHold()
      holdTimer = setTimeout(() => {
        holdTimer = null
        if (keyIsDown && !otherKeyPressed) {
          isHolding = true
          cb.onHoldStart()
        }
      }, HOLD_DELAY_MS)
    },

    otherKeyDown() {
      if (keyIsDown) otherKeyPressed = true
    },

    keyUp() {
      const holdDuration = Date.now() - keyDownTime
      keyIsDown = false
      clearHold()

      if (isHolding) {
        isHolding = false
        cb.onHoldEnd()
        return
      }

      // Not a bare quick tap (too long, or a shortcut like Ctrl+C) → ignore.
      if (holdDuration >= TAP_MAX_HOLD_MS || otherKeyPressed) {
        otherKeyPressed = false
        return
      }

      if (Date.now() - lastTapUpTime < DOUBLE_TAP_MS) {
        // Second quick tap → double-tap.
        lastTapUpTime = NO_TAP
        cb.onDoubleTap()
        return
      }

      // First tap: a second one inside the window makes it a double-tap.
      lastTapUpTime = Date.now()
    },

    blur() {
      clearHold()
      keyIsDown = false
      if (isHolding) {
        isHolding = false
        cb.onCancel()
      }
    },

    dispose() {
      clearHold()
      keyIsDown = false
      isHolding = false
      lastTapUpTime = NO_TAP
    },
  }
}
